import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import dayjs from 'dayjs';
import { Box, Button, CircularProgress, Stack, TextField, Typography } from '@mui/material';
import { IconLogout } from '@tabler/icons-react';

import ApiHelper from 'utils/apiHelper';
import { API_URL } from 'utils/constants';
import { logout } from 'components/../redux/actions/auth';
import raptorLogo from 'assets/images/raptor_tech_logo.png';

const NAVY = '#1e4161';
const ORANGE = '#f27d00';
const GREEN = '#137a13';
const GREY = '#9e9e9e';

// SSOWakeAPI wants Python's '%Y/%m/%d %H:%M:%S %z'.
const startTimeFormat = 'YYYY/MM/DD HH:mm:ss ZZ';

// SSOWakeAPI answers with a json.dumps'd list of [name, state] pairs, so the body is
// a JSON string rather than a JSON array. Error cases ('No Devices Found!', an
// unknown organization, ...) come back as a plain string instead of a list.
const parseWakeList = (payload) => {
    let parsed = payload;
    if (typeof parsed === 'string') {
        try {
            parsed = JSON.parse(parsed);
        } catch (err) {
            return [];
        }
    }
    return Array.isArray(parsed) ? parsed : [];
};

const isAsleep = (state) => String(state ?? '').toLowerCase() === 'asleep';
const deviceKey = (name) => String(name ?? '').toLowerCase();

const isTimeValue = (value) => /\d/.test(String(value ?? ''));

const WakePortal = () => {
    const dispatch = useDispatch();
    const { userInfo } = useSelector((state) => state.auth);
    const user = userInfo?.user;
    const organizationName = user?.orgid?.name ?? '';

    const [query, setQuery] = useState('');
    const [devices, setDevices] = useState([]);
    const [notFound, setNotFound] = useState(false);
    const [searched, setSearched] = useState(false);
    // True when the current list came from the 'list everything I can wake' search,
    // so Refresh knows which call to repeat.
    const [listedAll, setListedAll] = useState(false);
    // Edited the search box since the last search - the button goes back to 'Search'.
    const [dirty, setDirty] = useState(false);
    // What the last wake answered for each device, keyed by name: either the expected
    // wake time or the power state the API reported back instead.
    const [wakeResults, setWakeResults] = useState({});
    const [searching, setSearching] = useState(false);
    const [wakingName, setWakingName] = useState(null);
    const [error, setError] = useState('');

    const busy = searching || wakingName !== null;

    // A wake that came back with a time means one is scheduled, so the device reads
    // 'Waking…' until its status stops coming back asleep.
    const expectedWakeTime = (device) => {
        const result = wakeResults[deviceKey(device.name)];
        return result !== undefined && isTimeValue(result) ? result : null;
    };
    const isDeviceWaking = (device) => expectedWakeTime(device) !== null && isAsleep(device.state);
    // A wake that came back with a state queued nothing - that state is reported as-is
    // rather than pretending the machine is on its way up.
    const reportedState = (device) => {
        const result = wakeResults[deviceKey(device.name)];
        return result !== undefined && isTimeValue(result) === false ? result : null;
    };
    const canWakeDevice = (device) => isAsleep(device.state) && wakeResults[deviceKey(device.name)] === undefined;

    const showRefresh = devices.some(isDeviceWaking) && !dirty;
    const searchLabel = showRefresh ? 'Refresh' : 'Search';
    const anyWakeButtonShown = devices.some(canWakeDevice);

    // GET is the read-only half of the endpoint: it reports state and never wakes
    // anything. The organization and requester come from the auth token, so only the
    // device scope is sent.
    const fetchStatus = async (deviceName) => {
        const response = await ApiHelper.get(API_URL.SSO_WAKE, {}, {}, { devices: deviceName });
        return parseWakeList(response?.data);
    };

    // POST is the half that acts: it queues the wake.
    const sendWake = async (deviceName) => {
        const response = await ApiHelper.post(API_URL.SSO_WAKE, {
            devices: deviceName,
            startTime: dayjs().format(startTimeFormat)
        });
        return parseWakeList(response?.data);
    };

    // Sending no device name asks for every device associated with this user.
    const fetchAllDevices = async () => {
        const found = await fetchStatus('');
        return found.map((entry) => ({ name: entry[0], state: entry[1] }));
    };

    const fetchOneDevice = async (deviceName) => {
        const [match] = await fetchStatus(deviceName);
        return match ? [{ name: match[0], state: match[1] }] : [];
    };

    // Search and Refresh only ever ask for status, so neither wakes a machine.
    const runSearch = async ({ all, deviceName }) => {
        setSearching(true);
        setError('');
        try {
            const found = all ? await fetchAllDevices() : await fetchOneDevice(deviceName);
            setDevices(found);
            setWakeResults((previous) => {
                const kept = {};
                Object.entries(previous).forEach(([key, result]) => {
                    // A wake that queued nothing is dropped so the device can be tried again.
                    if (isTimeValue(result) === false) {
                        return;
                    }
                    const refreshed = found.find((device) => deviceKey(device.name) === key);
                    if (refreshed !== undefined && isAsleep(refreshed.state) === false) {
                        return;
                    }
                    kept[key] = result;
                });
                return kept;
            });
            setListedAll(all);
            setNotFound(!all && found.length === 0);
            setSearched(true);
            setDirty(false);
        } catch (err) {
            setError('We could not reach the server. Please try again.');
        } finally {
            setSearching(false);
        }
    };

    const onSubmitSearch = (event) => {
        event.preventDefault();
        if (showRefresh) {
            // Repeat whichever search produced the list currently on screen.
            runSearch(listedAll ? { all: true } : { all: false, deviceName: devices[0].name });
            return;
        }
        const deviceName = query.trim();
        // An empty box means 'show me everything I am allowed to wake'.
        runSearch(deviceName === '' ? { all: true } : { all: false, deviceName });
    };

    const onClickWake = async (device) => {
        setWakingName(device.name);
        setError('');
        try {
            const [result] = await sendWake(device.name);
            setWakeResults((previous) => (result ? { ...previous, [deviceKey(device.name)]: result[1] } : previous));
        } catch (err) {
            setError('We could not send the wake request. Please try again.');
        } finally {
            setWakingName(null);
        }
    };

    const deviceButtonLabel = (device) => {
        if (isDeviceWaking(device)) {
            return 'Waking…';
        }
        if (canWakeDevice(device)) {
            return 'Wake';
        }

        return reportedState(device) ?? device.state;
    };

    const waitNotice = '';

    return (
        <Box
            sx={{
                minHeight: '100vh',
                display: 'flex',
                background: `linear-gradient(135deg, ${NAVY} 0%, #6b88a3 10%, #ffffff 22%, #ffffff 100%)`
            }}
        >
            <Box sx={{ width: 180, flexShrink: 0, pt: 2, textAlign: 'center' }}>
                <Box component="img" src={raptorLogo} alt="Raptor Technical LLC" sx={{ width: 104 }} />
                <Typography sx={{ color: '#ffffff', fontSize: 12, mt: 1 }}>Raptor Technical LLC</Typography>
            </Box>

            <Box sx={{ flexGrow: 1, px: { xs: 2, md: 5 }, py: 3 }}>
                {/* Logout is the only control here - deliberately no links anywhere else. */}
                <Stack direction="row" justifyContent="flex-end">
                    <Button
                        onClick={() => dispatch(logout())}
                        startIcon={<IconLogout size="1.1rem" stroke={1.5} />}
                        sx={{ color: NAVY, textTransform: 'none', fontSize: 14 }}
                    >
                        Logout
                    </Button>
                </Stack>

                <Box sx={{ maxWidth: 760, mt: 2 }}>
                    <Typography align="center" sx={{ color: ORANGE, fontSize: 26, fontWeight: 700 }}>
                        {organizationName}
                    </Typography>
                    <Stack direction="row" spacing={1} alignItems="baseline" justifyContent="center" sx={{ mt: 1 }}>
                        <Typography sx={{ fontSize: 24, fontWeight: 600, color: '#000000' }}>Computer Wake Portal</Typography>
                        <Typography sx={{ fontSize: 13, color: '#000000' }}>Powered by WakeSMART</Typography>
                    </Stack>

                    <Box component="form" onSubmit={onSubmitSearch} sx={{ mt: 6, ml: { xs: 0, md: 2 } }}>
                        <Typography sx={{ fontSize: 14, color: '#000000' }}>Computer to wake</Typography>
                        <Stack direction="row" spacing={2} alignItems="center" sx={{ mt: 0.5 }}>
                            <TextField
                                value={query}
                                onChange={(event) => {
                                    setQuery(event.target.value);
                                    setDirty(true);
                                }}
                                size="small"
                                inputProps={{ 'aria-label': 'Computer to wake' }}
                                sx={{ width: 265, backgroundColor: '#ffffff' }}
                            />
                            <Button
                                type="submit"
                                variant="contained"
                                disableElevation
                                disabled={busy}
                                sx={{
                                    backgroundColor: GREEN,
                                    minWidth: 105,
                                    '&:hover': { backgroundColor: '#0f5f0f' }
                                }}
                            >
                                {searching ? <CircularProgress size={18} sx={{ color: '#ffffff' }} /> : searchLabel}
                            </Button>
                            <Typography sx={{ fontSize: 13, color: '#000000', maxWidth: 230 }}>
                                Hit &apos;{searchLabel}&apos; to find your computer or refresh your device&apos;s status
                            </Typography>
                        </Stack>

                        {notFound && (
                            <Typography sx={{ mt: 2, fontSize: 15, fontWeight: 600, color: '#000000' }}>Device not found</Typography>
                        )}

                        {searched && listedAll && devices.length === 0 && (
                            <Typography sx={{ mt: 2, fontSize: 15, fontWeight: 600, color: '#000000' }}>
                                No devices available to wake
                            </Typography>
                        )}

                        <Box sx={{ maxHeight: 360, overflowY: 'auto', mt: devices.length > 0 ? 2 : 0 }}>
                            {devices.map((device) => (
                                <Stack key={deviceKey(device.name)} direction="row" spacing={2} alignItems="center" sx={{ mb: 1 }}>
                                    <Button
                                        variant="contained"
                                        disableElevation
                                        onClick={() => onClickWake(device)}
                                        disabled={!canWakeDevice(device) || busy}
                                        sx={{
                                            minWidth: 105,
                                            backgroundColor: canWakeDevice(device) ? GREEN : GREY,
                                            color: '#ffffff',
                                            '&:hover': { backgroundColor: canWakeDevice(device) ? '#0f5f0f' : GREY },
                                            '&.Mui-disabled': { backgroundColor: GREY, color: '#ffffff' }
                                        }}
                                    >
                                        {wakingName === device.name ? (
                                            <CircularProgress size={18} sx={{ color: '#ffffff' }} />
                                        ) : (
                                            deviceButtonLabel(device)
                                        )}
                                    </Button>
                                    <Typography sx={{ fontSize: 15, fontWeight: 600, color: '#000000' }}>{device.name}</Typography>
                                    {isDeviceWaking(device) && (
                                        <Typography sx={{ fontSize: 13, color: '#000000' }}>
                                            Expected to wake at {expectedWakeTime(device)}
                                        </Typography>
                                    )}
                                    {devices.length === 1 && canWakeDevice(device) && (
                                        <Typography sx={{ fontSize: 13, color: '#000000', maxWidth: 230 }}>{waitNotice}</Typography>
                                    )}
                                </Stack>
                            ))}
                        </Box>

                        {devices.length > 1 && anyWakeButtonShown && (
                            <Typography sx={{ mt: 1, fontSize: 13, color: '#000000' }}>{waitNotice}</Typography>
                        )}

                        {error && <Typography sx={{ mt: 2, fontSize: 13, color: '#b00020' }}>{error}</Typography>}
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};

export default WakePortal;
